
import React, { useState } from "react";
import { useParams,useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "./convex/_generated/api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import "./PersonalLifestylePage.css";

export default function PersonalLifestylePage() {
  const { progressId, userId } = useParams();
  const navigate = useNavigate();

  const [personalCareMinutes, setPersonalCareMinutes] = useState(0);
  const [hobbiesMinutes, setHobbiesMinutes] = useState(0);
  const [outdoorMinutes, setOutdoorMinutes] = useState(0);
  const [relaxationMinutes, setRelaxationMinutes] = useState(0);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

   const premiumUser = useQuery(
    api.users.getPremiumUserById,
    userId
      ? { userId }
      : "skip"
  );
  
  

  const calculatePersonalLifestyle = useMutation(
    api.progress.calculatePersonalLifestyle
  );

  const usualresult = useQuery(
    api.progress.getPersonalLifestyleResults,
    progressId && userId
      ? {
          progressId,
          userId,
        }
      : "skip"
  );

  console.log(
    "PERSONAL LIFESTYLE USUAL RESULT:",
    usualresult
  );

  if (premiumUser === null) {
    navigate("/auth");
    return null;
  }

  const dailyRecords = usualresult?.dailyRecords ?? [];

  const weeklyRecords = usualresult?.weeklyRecords ?? [];

  const sortedDailyRecords = [...dailyRecords].sort(
    (a, b) => a.date.localeCompare(b.date)
  );

  const sortedWeeklyRecords = [...weeklyRecords].sort(
    (a, b) => a.weekStart.localeCompare(b.weekStart)
  );

  const latestWeeklyRecord =
    sortedWeeklyRecords.length > 0
      ? sortedWeeklyRecords[
          sortedWeeklyRecords.length - 1
        ]
      : null;

  const safeNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : 0;
  };

  const dailyScoreChartData =
    sortedDailyRecords.map((record) => ({
      date: record.date,
      dailyScore: Math.round(
        Math.max(
          0,
          Math.min(
            100,
            safeNumber(record.dailyScore)
          )
        )
      ),
    }));

  console.log(
    "PERSONAL DAILY RECORDS:",
    dailyRecords
  );

  console.log(
    "PERSONAL DAILY CHART DATA:",
    dailyScoreChartData
  );

  const weeklyScoreChartData =
    sortedWeeklyRecords.map((record) => ({
      week: record.weekStart,

      trackingScore: Math.round(
        Math.max(
          0,
          Math.min(
            100,
            safeNumber(record.trackingScore)
          )
        )
      ),

      activityScore: Math.round(
        Math.max(
          0,
          Math.min(
            100,
            safeNumber(record.activityScore)
          )
        )
      ),

      finalScore: Math.round(
        Math.max(
          0,
          Math.min(
            100,
            safeNumber(record.finalScore)
          )
        )
      ),
    }));

  console.log(
    "PERSONAL WEEKLY RECORDS:",
    weeklyRecords
  );

  console.log(
    "PERSONAL WEEKLY CHART DATA:",
    weeklyScoreChartData
  );

  const lifestyleMinutesData =
    latestWeeklyRecord
      ? [
          {
            name: "Personal Care",
            minutes: safeNumber(
              latestWeeklyRecord.totalPersonalCareMinutes
            ),
          },
          {
            name: "Hobbies",
            minutes: safeNumber(
              latestWeeklyRecord.totalHobbiesMinutes
            ),
          },
          {
            name: "Outdoor",
            minutes: safeNumber(
              latestWeeklyRecord.totalOutdoorMinutes
            ),
          },
          {
            name: "Relaxation",
            minutes: safeNumber(
              latestWeeklyRecord.totalRelaxationMinutes
            ),
          },
        ]
      : [];

  const getCurrentWeek = () => {
    const today = new Date();

    const day = today.getDay();

    const daysSinceMonday =
      day === 0 ? 6 : day - 1;

    const monday = new Date(today);

    monday.setDate(
      today.getDate() - daysSinceMonday
    );

    const sunday = new Date(monday);

    sunday.setDate(
      monday.getDate() + 6
    );

    return {
      weekStart:
        monday.toISOString().split("T")[0],

      weekEnd:
        sunday.toISOString().split("T")[0],
    };
  };

  const handleCalculate = async () => {
    setError("");
    setResult(null);

    if (!progressId || !userId) {
      setError(
        "Missing progress or user information."
      );
      return;
    }

    const personalCare =
      Number(personalCareMinutes);

    const hobbies =
      Number(hobbiesMinutes);

    const outdoor =
      Number(outdoorMinutes);

    const relaxation =
      Number(relaxationMinutes);

    if (
      !Number.isFinite(personalCare) ||
      !Number.isFinite(hobbies) ||
      !Number.isFinite(outdoor) ||
      !Number.isFinite(relaxation)
    ) {
      setError(
        "Please enter valid numbers."
      );
      return;
    }

    if (
      personalCare < 0 ||
      hobbies < 0 ||
      outdoor < 0 ||
      relaxation < 0
    ) {
      setError(
        "Minutes cannot be negative."
      );
      return;
    }

    try {
      setLoading(true);

      const today =
        new Date().toISOString().split("T")[0];

      const {
        weekStart,
        weekEnd,
      } = getCurrentWeek();

      const response =
        await calculatePersonalLifestyle({
          userId,
          progressId,

          date: today,

          personalCareMinutes:
            personalCare,

          hobbiesMinutes:
            hobbies,

          outdoorMinutes:
            outdoor,

          relaxationMinutes:
            relaxation,

          weekStart,
          weekEnd,
        });

      console.log(
        "Personal & Lifestyle Result:",
        response
      );

      setResult({
        ...response,
        date: today,
      });
    } catch (err) {
      console.error(
        "Personal & Lifestyle Error:",
        err
      );

      setError(
        err?.message ||
          "Failed to calculate Personal & Lifestyle score."
      );
    } finally {
      setLoading(false);
    }
  };

  const scoreChartData = result
    ? [
        {
          name: "Tracking",
          score: Math.round(
            Math.max(
              0,
              Math.min(
                100,
                safeNumber(
                  result.trackingScore
                )
              )
            )
          ),
        },

        {
          name: "Activities",
          score: Math.round(
            Math.max(
              0,
              Math.min(
                100,
                safeNumber(
                  result.activityScore
                )
              )
            )
          ),
        },

        {
          name: "Final",
          score: Math.round(
            Math.max(
              0,
              Math.min(
                100,
                safeNumber(
                  result.finalScore
                )
              )
            )
          ),
        },
      ]
    : [];

  return (
    <div className="personal-lifestyle-page">
      <div className="personal-lifestyle-container">

        {/* HEADER */}
        <div className="personal-lifestyle-header">
          <h1>
            Personal & Lifestyle
          </h1>

          <p>
            Track today's personal habits and
            calculate your weekly score.
          </p>
        </div>

        {/* DAILY TRACKING */}
        <div className="lifestyle-form-card">

          <h2>
            Today's Tracking
          </h2>

          <div className="lifestyle-input-grid">

            {/* PERSONAL CARE */}
            <div className="lifestyle-input-group">
              <label>
                Personal Care (minutes)
              </label>

              <input
                type="number"
                min="0"
                value={personalCareMinutes}
                onChange={(e) =>
                  setPersonalCareMinutes(
                    Number(e.target.value)
                  )
                }
              />
            </div>

            {/* HOBBIES */}
            <div className="lifestyle-input-group">
              <label>
                Hobbies (minutes)
              </label>

              <input
                type="number"
                min="0"
                value={hobbiesMinutes}
                onChange={(e) =>
                  setHobbiesMinutes(
                    Number(e.target.value)
                  )
                }
              />
            </div>

            {/* OUTDOOR */}
            <div className="lifestyle-input-group">
              <label>
                Outdoor Time (minutes)
              </label>

              <input
                type="number"
                min="0"
                value={outdoorMinutes}
                onChange={(e) =>
                  setOutdoorMinutes(
                    Number(e.target.value)
                  )
                }
              />
            </div>

            {/* RELAXATION */}
            <div className="lifestyle-input-group">
              <label>
                Relaxation (minutes)
              </label>

              <input
                type="number"
                min="0"
                value={relaxationMinutes}
                onChange={(e) =>
                  setRelaxationMinutes(
                    Number(e.target.value)
                  )
                }
              />
            </div>

          </div>
        </div>

        {/* CALCULATE */}
        <div className="lifestyle-action">
          <button
            onClick={handleCalculate}
            disabled={loading}
            className="lifestyle-submit"
          >
            {loading
              ? "Calculating..."
              : "Save & Calculate Weekly Score"}
          </button>
        </div>

        {/* ERROR */}
        {error && (
          <div className="lifestyle-error">
            {error}
          </div>
        )}

        {/* CURRENT RESULT */}
        {result && (
          <div className="lifestyle-results">

            {/* DAILY SCORE */}
            <div className="lifestyle-card daily-score-card">

              <p className="score-label">
                Today's Score
              </p>

              <p className="daily-score-value">
                {Math.round(
                  safeNumber(
                    result.dailyScore
                  )
                )}
              </p>

            </div>

            {/* WEEKLY SCORE */}
            <div className="weekly-score-section">

              <h2>
                Weekly Score
              </h2>

              <div className="weekly-score-grid">

                <div className="score-card tracking-card">
                  <p className="score-label">
                    Tracking Score
                  </p>

                  <p className="score-value">
                    {Math.round(
                      safeNumber(
                        result.trackingScore
                      )
                    )}
                  </p>
                </div>

                <div className="score-card activity-card">
                  <p className="score-label">
                    Activity Score
                  </p>

                  <p className="score-value">
                    {Math.round(
                      safeNumber(
                        result.activityScore
                      )
                    )}
                  </p>
                </div>

                <div className="score-card final-card">
                  <p className="score-label">
                    Final Score
                  </p>

                  <p className="score-value">
                    {Math.round(
                      safeNumber(
                        result.finalScore
                      )
                    )}
                  </p>
                </div>

              </div>
            </div>

            {/* CURRENT SCORE COMPARISON */}
            {scoreChartData.length > 0 && (
              <div className="lifestyle-card chart-card">

                <h2>
                  Current Score Comparison
                </h2>

                <div className="chart-container">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <BarChart
                      data={scoreChartData}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#26372d"
                      />

                      <XAxis
                        dataKey="name"
                        stroke="#94a3b8"
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={{
                          stroke: "#26372d",
                        }}
                        tickLine={false}
                      />

                      <YAxis
                        domain={[0, 100]}
                        stroke="#94a3b8"
                        tick={{
                          fill: "#94a3b8",
                          fontSize: 12,
                        }}
                        axisLine={false}
                        tickLine={false}
                      />

                      <Tooltip
                        contentStyle={{
                          background: "#07100a",
                          border:
                            "1px solid rgba(74, 222, 128, 0.25)",
                          borderRadius: "12px",
                          color: "#f8fafc",
                        }}
                      />

                      <Legend />

                      <Bar
                        dataKey="score"
                        name="Score"
                        fill="#22c55e"
                        radius={[
                          7,
                          7,
                          0,
                          0,
                        ]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

              </div>
            )}

          </div>
        )}

        {/* DAILY SCORE HISTORY */}
        {dailyScoreChartData.length > 0 && (
          <div className="lifestyle-card chart-card">

            <h2>
              Daily Personal & Lifestyle Scores
            </h2>

            <div className="chart-container">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={dailyScoreChartData}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26372d"
                  />

                  <XAxis
                    dataKey="date"
                    stroke="#94a3b8"
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#26372d",
                    }}
                    tickLine={false}
                  />

                  <YAxis
                    domain={[0, 100]}
                    stroke="#94a3b8"
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      background: "#07100a",
                      border:
                        "1px solid rgba(74, 222, 128, 0.25)",
                      borderRadius: "12px",
                      color: "#f8fafc",
                    }}
                  />

                  <Legend />

                  <Bar
                    dataKey="dailyScore"
                    name="Daily Score"
                    fill="#4ade80"
                    radius={[
                      7,
                      7,
                      0,
                      0,
                    ]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

          </div>
        )}

        {/* WEEKLY SCORE HISTORY */}
        {weeklyScoreChartData.length > 0 && (
          <div className="lifestyle-card chart-card">

            <h2>
              Weekly Personal & Lifestyle Scores
            </h2>

            <div className="chart-container">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={weeklyScoreChartData}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26372d"
                  />

                  <XAxis
                    dataKey="week"
                    stroke="#94a3b8"
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#26372d",
                    }}
                    tickLine={false}
                  />

                  <YAxis
                    domain={[0, 100]}
                    stroke="#94a3b8"
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      background: "#07100a",
                      border:
                        "1px solid rgba(74, 222, 128, 0.25)",
                      borderRadius: "12px",
                      color: "#f8fafc",
                    }}
                  />

                  <Legend />

                  <Bar
                    dataKey="trackingScore"
                    name="Tracking Score"
                    fill="#22c55e"
                    radius={[
                      7,
                      7,
                      0,
                      0,
                    ]}
                  />

                  <Bar
                    dataKey="activityScore"
                    name="Activity Score"
                    fill="#84cc16"
                    radius={[
                      7,
                      7,
                      0,
                      0,
                    ]}
                  />

                  <Bar
                    dataKey="finalScore"
                    name="Final Score"
                    fill="#f59e0b"
                    radius={[
                      7,
                      7,
                      0,
                      0,
                    ]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

          </div>
        )}

        {/* LIFESTYLE ACTIVITY HISTORY */}
        {lifestyleMinutesData.length > 0 && (
          <div className="lifestyle-card chart-card">

            <h2>
              Average Lifestyle Time
            </h2>

            <div className="chart-container">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={lifestyleMinutesData}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26372d"
                  />

                  <XAxis
                    dataKey="name"
                    stroke="#94a3b8"
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#26372d",
                    }}
                    tickLine={false}
                  />

                  <YAxis
                    stroke="#94a3b8"
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      background: "#07100a",
                      border:
                        "1px solid rgba(74, 222, 128, 0.25)",
                      borderRadius: "12px",
                      color: "#f8fafc",
                    }}
                  />

                  <Legend />

                  <Bar
                    dataKey="minutes"
                    name="Minutes per Day"
                    fill="#4ade80"
                    radius={[
                      7,
                      7,
                      0,
                      0,
                    ]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

          </div>
        )}

        {/* WEEKLY DETAILS */}
        {latestWeeklyRecord && (
          <div className="lifestyle-card weekly-details-card">

            <h2>
              Weekly Details
            </h2>

            <div className="details-grid">

              <p>
                Days measured:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord.daysMeasured
                  )}
                </strong>
              </p>

              <p>
                Personal & Lifestyle suggestions:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord.suggestionCount
                  )}
                </strong>
              </p>

              <p>
                Average personal care:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord.averagePersonalCareMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

              <p>
                Average hobbies:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord.averageHobbiesMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

              <p>
                Average outdoor time:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord.averageOutdoorMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

              <p>
                Average relaxation:{" "}
                <strong>
                  {safeNumber(
                    latestWeeklyRecord.averageRelaxationMinutes
                  ).toFixed(1)}{" "}
                  min/day
                </strong>
              </p>

            </div>
          </div>
        )}

      </div>
    </div>
  );
}
